<template>
  <div class="proxima_nova credits_usage_root">
    <div class="flex flex-col gap-8 px-4 py-2">
      <span class="text-[24px] font-semibold text_color">Credits Usage</span>

      <div class="flex flex-col gap-3">
        <span class="text-[18px] font-semibold text_color">Overview</span>
        <div class="flex flex-col gap-1">
          <span class="text-sm secondary_color">Total Credits Remaining</span>
          <div class="flex flex-row items-baseline gap-2">
            <span class="text-[32px] font-bold text_color">{{ AICredits.toLocaleString() }}</span>
            <span class="text-base secondary_color">credits</span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-5">
        <span class="text-[18px] font-semibold text_color">Consumption</span>

        <div class="flex flex-row gap-2 flex-wrap items-center">
          <a-dropdown :trigger="['click']" placement="bottomLeft" overlay-class-name="usage_dropdown_overlay">
            <div class="filter_chip cursor-pointer">
              <span class="text-sm font-semibold text_color">Period:</span>
              <span class="text-sm secondary_color">{{ formattedSelectedPeriod }}</span>
              <ChevronIcon class="size-4 chip_arrow" />
            </div>
            <template #overlay>
              <PeriodMonthPicker v-model:year="selectedYear" v-model:month="selectedMonth" @select="handlePeriodSelect" />
            </template>
          </a-dropdown>

          <a-popover v-model:open="appPopoverOpen" :trigger="['click']" placement="bottomLeft" overlay-class-name="usage_popover_overlay" @open-change="handleAppPopoverOpen">
            <div class="filter_chip cursor-pointer">
              <span class="text-sm font-semibold text_color">Action:</span>
              <span class="text-sm secondary_color">{{ appChipText }}</span>
              <ChevronIcon class="size-4 chip_arrow" />
            </div>
            <template #content>
              <div class="search_select_panel">
                <div class="search_input_wrapper">
                  <SearchIcon class="size-4 search_icon" />
                  <input ref="appSearchRef" v-model="appSearchQuery" type="text" class="search_input" placeholder="Search actions" @input="handleAppSearchInput" />
                </div>
                <div class="search_select_list">
                  <div v-if="appSearching" class="search_state_block">
                    <CustomLoader :size="20" />
                  </div>
                  <div v-else-if="filteredAppGroups.length === 0" class="search_state_block">
                    <EmptyResultsIcon class="empty_illustration" />
                    <span class="text-[13px] secondary_color">No results found!</span>
                  </div>
                  <template v-else>
                    <div v-for="group in filteredAppGroups" :key="group.key" class="app_group">
                      <div class="app_group_header" @click="toggleGroup(group)">
                        <span class="search_select_check">
                          <CheckIcon v-if="groupState(group) === 'all'" class="size-4 text_color" />
                          <DashIcon v-else-if="groupState(group) === 'some'" class="size-4 text_color" />
                        </span>
                        <span class="text-[13px] font-semibold text_color">{{ group.label }}</span>
                      </div>
                      <div
                        v-for="title in group.titles"
                        :key="title"
                        :class="['search_select_row', 'search_select_row_indented', selectedTitles.includes(title) && 'search_select_row_active']"
                        @click.stop="toggleTitle(title)"
                      >
                        <span class="search_select_check">
                          <CheckIcon v-if="selectedTitles.includes(title)" class="size-4 text_color" />
                        </span>
                        <span class="text-[13px] text_color">{{ title }}</span>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </a-popover>

          <a-popover v-model:open="userPopoverOpen" :trigger="['click']" placement="bottomLeft" overlay-class-name="usage_popover_overlay" @open-change="handleUserPopoverOpen">
            <div class="filter_chip cursor-pointer">
              <span class="text-sm font-semibold text_color">User:</span>
              <span class="text-sm secondary_color">{{ userChipText }}</span>
              <ChevronIcon class="size-4 chip_arrow" />
            </div>
            <template #content>
              <div class="search_select_panel">
                <div class="search_input_wrapper">
                  <SearchIcon class="size-4 search_icon" />
                  <input ref="userSearchRef" v-model="userSearchQuery" type="text" class="search_input" placeholder="Search users" @input="handleUserSearchInput" />
                </div>
                <div class="search_select_list">
                  <div v-if="userSearching" class="search_state_block">
                    <CustomLoader :size="20" />
                  </div>
                  <div v-else-if="filteredUserOptions.length === 0" class="search_state_block">
                    <EmptyResultsIcon class="empty_illustration" />
                    <span class="text-[13px] secondary_color">No results found!</span>
                  </div>
                  <template v-else>
                    <div v-for="user in filteredUserOptions" :key="user.id" :class="['search_select_row', selectedUsers.includes(user.id) && 'search_select_row_active']" @click="toggleUser(user.id)">
                      <span class="search_select_check">
                        <CheckIcon v-if="selectedUsers.includes(user.id)" class="size-4 text_color" />
                      </span>
                      <span class="text-[13px] text_color">{{ user.name }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </a-popover>
        </div>

        <div class="chart_card">
          <div class="flex flex-col gap-1 mb-2">
            <span class="text-base font-semibold text_color">Credits Consumption</span>
            <span class="text-sm secondary_color">{{ formattedSelectedPeriod }}</span>
          </div>
          <div v-if="loading" class="flex items-center justify-center" style="min-height: 320px">
            <CustomLoader :size="28" />
          </div>
          <div v-else ref="chartRef" class="chart_container"></div>
        </div>

        <div v-if="!loading && AIActions.length > 0" class="action_table">
          <div class="action_table_header">
            <span class="action_table_cell text-sm font-semibold secondary_color">Action</span>
            <span class="action_table_cell text-sm font-semibold secondary_color">Credits Consumed</span>
          </div>
          <div v-for="row in AIActions" :key="row.title" class="action_table_row">
            <span class="action_table_cell text-sm text_color">{{ row.title }}</span>
            <span class="action_table_cell text-sm text_color">{{ Number(row.credits_consumed).toLocaleString() }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch, onBeforeUnmount, h } from 'vue';
import ApexCharts from 'apexcharts';
import CustomLoader from '@/components/custom/CustomLoader.vue';
import DashboardService from '@/services/DashboardService';
import PeriodMonthPicker from '@/components/billing/PeriodMonthPicker.vue';

const ChevronIcon = {
  render() {
    return h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
      h('polyline', { points: '6 9 12 15 18 9' }),
    ]);
  },
};

const CheckIcon = {
  render() {
    return h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '3', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
      h('polyline', { points: '20 6 9 17 4 12' }),
    ]);
  },
};

const DashIcon = {
  render() {
    return h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '3', 'stroke-linecap': 'round' }, [h('line', { x1: '5', y1: '12', x2: '19', y2: '12' })]);
  },
};

const SearchIcon = {
  render() {
    return h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
      h('circle', { cx: '11', cy: '11', r: '8' }),
      h('line', { x1: '21', y1: '21', x2: '16.65', y2: '16.65' }),
    ]);
  },
};

const EmptyResultsIcon = {
  render() {
    return h('svg', { viewBox: '0 0 64 64', fill: 'none', 'aria-hidden': 'true' }, [
      h('rect', { x: '14', y: '18', width: '32', height: '32', rx: '4', fill: 'rgba(0,0,0,0.06)', stroke: 'rgba(0,0,0,0.18)', 'stroke-width': '1.5' }),
      h('rect', { x: '22', y: '10', width: '32', height: '32', rx: '4', fill: 'rgba(0,0,0,0.04)', stroke: 'rgba(0,0,0,0.22)', 'stroke-width': '1.5' }),
    ]);
  },
};

// Sentinel auth_user_ids emitted by the backend: -555 = AI agent context
// (AuthUser::AI_USER_ID on super_sales), -1 = caller did not supply a user.
// Always prepended to the user list so they're filterable.
const SENTINEL_USERS = [
  { id: -555, name: 'AI Agent' },
  { id: -1, name: 'Unknown' },
];

const CHART_COLORS = ['#7095eb', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#E6A700', '#00A6D6', '#D64500', '#5F27CD'];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const now = new Date();
const selectedYear = ref(now.getFullYear());
const selectedMonth = ref(now.getMonth());
const selectedTitles = ref([]);
const selectedUsers = ref([]);
const appSearchQuery = ref('');
const userSearchQuery = ref('');
const appSearchRef = ref(null);
const userSearchRef = ref(null);
const appPopoverOpen = ref(false);
const userPopoverOpen = ref(false);
const appSearching = ref(false);
const userSearching = ref(false);
let appSearchTimer = null;
let userSearchTimer = null;
const SEARCH_DEBOUNCE_MS = 300;

const loading = ref(false);
const AICredits = ref(0);
// Raw history payload — kept so the client-side title filter can re-derive
// chart + table without refetching. action_meta drives both the filter
// dropdown grouping and the table aggregation.
const rawHistory = ref([]);
const actionMeta = ref({}); // title → { action_type, app_code }
// Derived state the template binds to.
const AIActions = ref([]);
const date_values = ref([]);
const data_series = ref([]);
// userOptions: super_sales' /api/get_users + sentinel rows prepended.
const userOptions = ref([...SENTINEL_USERS]);

const chartRef = ref(null);
let chartInstance = null;

const formattedSelectedPeriod = computed(() => `${MONTH_NAMES[selectedMonth.value]} ${selectedYear.value}`);

const appChipText = computed(() => {
  if (selectedTitles.value.length === 0) return 'All app actions';
  if (selectedTitles.value.length === 1) return selectedTitles.value[0];
  return `${selectedTitles.value.length} selected`;
});

const userChipText = computed(() => {
  if (selectedUsers.value.length === 0) return 'All users';
  if (selectedUsers.value.length === 1) {
    return userOptions.value.find((u) => u.id === selectedUsers.value[0])?.name || '';
  }
  return `${selectedUsers.value.length} selected`;
});

// Acronyms ≤3 chars (CRM, AI) stay uppercase; longer tokens get title-cased.
// Driven entirely by action_meta — no static frontend mapping.
const formatAppCode = (code) => {
  if (!code) return '';
  return code
    .split('_')
    .map((w) => (w.length <= 3 ? w : w[0] + w.slice(1).toLowerCase()))
    .join(' ');
};

// Groups derived from action_meta. Titles with a non-empty app_code group
// under their app; empty-app_code titles split by action_type into two
// "Other" buckets so users can still bulk-select them.
const appGroups = computed(() => {
  const meta = actionMeta.value || {};
  const groups = new Map();
  for (const [title, info] of Object.entries(meta)) {
    let key, label;
    if (info?.app_code) {
      key = info.app_code;
      label = formatAppCode(info.app_code);
    } else {
      key = '__OTHER__';
      label = 'Other';
    }
    if (!groups.has(key)) groups.set(key, { key, label, titles: [] });
    groups.get(key).titles.push(title);
  }
  const result = [...groups.values()];
  result.forEach((g) => g.titles.sort((a, b) => a.localeCompare(b)));
  // Push "Other" buckets to the bottom; alphabetise the rest.
  result.sort((a, b) => {
    const aOther = a.key.startsWith('__OTHER_');
    const bOther = b.key.startsWith('__OTHER_');
    if (aOther !== bOther) return aOther ? 1 : -1;
    return a.label.localeCompare(b.label);
  });
  return result;
});

const sortSelectedFirst = (items, isSelected) => {
  const selected = [];
  const rest = [];
  for (const item of items) {
    (isSelected(item) ? selected : rest).push(item);
  }
  return [...selected, ...rest];
};

const filteredAppGroups = computed(() => {
  const q = appSearchQuery.value.trim().toLowerCase();
  const result = [];
  for (const g of appGroups.value) {
    const visible = q ? g.titles.filter((t) => t.toLowerCase().includes(q)) : g.titles;
    if (visible.length === 0) continue;
    const sorted = sortSelectedFirst(visible, (t) => selectedTitles.value.includes(t));
    result.push({ key: g.key, label: g.label, titles: sorted, allTitles: g.titles });
  }
  return sortSelectedFirst(result, (g) => g.allTitles.some((t) => selectedTitles.value.includes(t)));
});

const groupState = (group) => {
  const sel = selectedTitles.value;
  let count = 0;
  for (const t of group.allTitles) if (sel.includes(t)) count++;
  if (count === 0) return 'none';
  if (count === group.allTitles.length) return 'all';
  return 'some';
};

const filteredUserOptions = computed(() => {
  const q = userSearchQuery.value.trim().toLowerCase();
  const filtered = q ? userOptions.value.filter((u) => u.name.toLowerCase().includes(q)) : userOptions.value;
  return sortSelectedFirst(filtered, (u) => selectedUsers.value.includes(u.id));
});

const handleAppPopoverOpen = (open) => {
  if (open) {
    nextTick(() => appSearchRef.value?.focus?.());
  } else {
    appSearchQuery.value = '';
    appSearching.value = false;
    if (appSearchTimer) clearTimeout(appSearchTimer);
  }
};

const handleUserPopoverOpen = (open) => {
  if (open) {
    nextTick(() => userSearchRef.value?.focus?.());
  } else {
    userSearchQuery.value = '';
    userSearching.value = false;
    if (userSearchTimer) clearTimeout(userSearchTimer);
  }
};

const handleAppSearchInput = () => {
  appSearching.value = true;
  if (appSearchTimer) clearTimeout(appSearchTimer);
  appSearchTimer = setTimeout(() => {
    appSearching.value = false;
  }, SEARCH_DEBOUNCE_MS);
};

const handleUserSearchInput = () => {
  userSearching.value = true;
  if (userSearchTimer) clearTimeout(userSearchTimer);
  userSearchTimer = setTimeout(() => {
    userSearching.value = false;
  }, SEARCH_DEBOUNCE_MS);
};

const toggleTitle = (title) => {
  const idx = selectedTitles.value.indexOf(title);
  if (idx === -1) selectedTitles.value.push(title);
  else selectedTitles.value.splice(idx, 1);
};

// Group toggle: if all members are already selected, remove them; otherwise
// add any missing. Reads from `allTitles` so it works during search.
const toggleGroup = (group) => {
  const all = group.allTitles;
  const sel = selectedTitles.value;
  const allSelected = all.every((t) => sel.includes(t));
  if (allSelected) {
    selectedTitles.value = sel.filter((t) => !all.includes(t));
  } else {
    const toAdd = all.filter((t) => !sel.includes(t));
    selectedTitles.value = [...sel, ...toAdd];
  }
};

const toggleUser = (id) => {
  const idx = selectedUsers.value.indexOf(id);
  if (idx === -1) selectedUsers.value.push(id);
  else selectedUsers.value.splice(idx, 1);
};

const handlePeriodSelect = ({ year, month }) => {
  selectedYear.value = year;
  selectedMonth.value = month;
};

const buildChartData = () => {
  const formatted_dates = date_values.value.map((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  });

  const dynamicColors = data_series.value.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]);
  const series = data_series.value.map((s) => ({
    name: s.name,
    type: s.type || 'column',
    data: s.data ? s.data.map((value, index) => ({ x: formatted_dates[index], y: value })) : [],
  }));

  return {
    chart: {
      height: 320,
      width: '100%',
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Proxima Nova, sans-serif',
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: '45%', borderRadius: 4, borderRadiusApplication: 'end', borderRadiusWhenStacked: 'last' },
    },
    colors: dynamicColors,
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    grid: { borderColor: 'rgba(128,128,128,0.15)', strokeDashArray: 4 },
    xaxis: {
      type: 'category',
      categories: formatted_dates,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: 'var(--secondary-color)', fontSize: '12px' } },
    },
    yaxis: {
      labels: {
        style: { colors: 'var(--secondary-color)', fontSize: '12px' },
        formatter: (v) => {
          if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
          return v;
        },
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'left',
      fontSize: '13px',
      labels: { colors: 'var(--secondary-color)' },
      markers: { width: 10, height: 10, radius: 5 },
      itemMargin: { horizontal: 12, vertical: 4 },
    },
    tooltip: { theme: 'light', y: { formatter: (v) => `${Number(v).toLocaleString()} credits` } },
    series,
  };
};

const renderChart = () => {
  nextTick(() => {
    if (!chartRef.value) return;
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
    chartInstance = new ApexCharts(chartRef.value, buildChartData());
    chartInstance.render();
  });
};

const buildFilterOpts = () => ({
  year: selectedYear.value,
  month: selectedMonth.value + 1, // JS months are 0-11; backend expects 1-12.
  authUserIds: selectedUsers.value,
});

// Re-derives chart series + action table from rawHistory using the current
// title selection. Empty selection = no filter (passthrough). Per-title
// totals are summed across every date in the history payload.
const applyAppFilter = () => {
  const selected = selectedTitles.value;
  const titleVisible = (title) => selected.length === 0 || selected.includes(title);

  const titleTotals = new Map();
  for (const day of rawHistory.value) {
    for (const [title, credits] of Object.entries(day.breakdown || {})) {
      const n = Number(credits);
      if (!(n > 0)) continue;
      if (!titleVisible(title)) continue;
      titleTotals.set(title, (titleTotals.get(title) || 0) + n);
    }
  }
  AIActions.value = Array.from(titleTotals.entries())
    .map(([title, credits_consumed]) => ({ title, credits_consumed }))
    .sort((x, y) => y.credits_consumed - x.credits_consumed);

  date_values.value = rawHistory.value.map((item) => item.date.split('T')[0]);
  const titles = [...new Set(rawHistory.value.flatMap((item) => Object.keys(item.breakdown || {})))].filter(titleVisible);
  data_series.value = titles.map((title) => ({
    name: title,
    type: 'column',
    data: rawHistory.value.map((day) => day.breakdown?.[title] || 0),
  }));
};

// Loads the workspace user list from super_sales once on mount and merges in
// the two sentinel rows. Intentionally not re-fetched on filter changes — the
// user catalog is stable for the session.
const fetchUsersOnce = async () => {
  try {
    const res = await DashboardService.fetchWorkspaceUsers('active');
    const rows = res?.users || [];
    const mapped = rows
      .map((u) => ({
        id: u.id,
        name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || `User ${u.id}`,
      }))
      .filter((u) => u.id != null);
    userOptions.value = [...SENTINEL_USERS, ...mapped];
  } catch (error) {
    console.error('Error fetching workspace users:', error);
  }
};

const fetchUsageData = async () => {
  loading.value = true;
  try {
    const opts = buildFilterOpts();

    const [overviewResponse, historyResponse] = await Promise.all([DashboardService.getCreditsOverview(), DashboardService.getAgentConsumptionHistory(opts)]);

    if (overviewResponse?.success) {
      AICredits.value = Math.floor(overviewResponse.remaining_agent_credits || 0);
    }

    if (historyResponse?.success) {
      rawHistory.value = historyResponse.history || [];
      actionMeta.value = historyResponse.action_meta || {};
    }

    // Title filter changes re-run applyAppFilter without hitting the
    // backend (see watch on selectedTitles below).
    applyAppFilter();
  } catch (error) {
    console.error('Error fetching credits usage data:', error);
  } finally {
    loading.value = false;
    renderChart();
  }
};

onMounted(async () => {
  await Promise.all([fetchUsersOnce(), fetchUsageData()]);
});

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
});

// Period + user filters change the backend query scope → refetch.
// Title filter is purely client-side, so it just re-derives chart + table
// from the cached rawHistory (no network round-trip on toggle).
watch(
  [selectedYear, selectedMonth, selectedUsers],
  () => {
    fetchUsageData();
  },
  { deep: true }
);

watch(
  selectedTitles,
  () => {
    applyAppFilter();
    renderChart();
  },
  { deep: true }
);
</script>

<style scoped>
.credits_usage_root {
  font-family: 'Proxima Nova', sans-serif;
}

.filter_chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: var(--white-opacity-4, rgba(0, 0, 0, 0.04));
  border: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.08));
  border-radius: 8px;
  user-select: none;
  transition: background-color 0.15s ease;
}

.filter_chip:hover {
  background-color: var(--white-opacity-8, rgba(0, 0, 0, 0.08));
}

.chip_arrow {
  color: var(--secondary-color);
}

.chart_card {
  background-color: var(--white-opacity-3, rgba(0, 0, 0, 0.03));
  border: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.08));
  border-radius: 12px;
  padding: 20px;
}

.chart_container {
  min-height: 320px;
  width: 100%;
}

.action_table {
  border: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.08));
  border-radius: 12px;
  overflow: hidden;
}

.action_table_header,
.action_table_row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 14px 20px;
}

.action_table_header {
  background-color: var(--white-opacity-4, rgba(0, 0, 0, 0.03));
  border-bottom: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.08));
}

.action_table_row:not(:last-child) {
  border-bottom: 1px solid var(--white-opacity-6, rgba(0, 0, 0, 0.05));
}

.action_table_cell {
  padding-right: 12px;
}

.search_select_panel {
  background-color: var(--dropdown-bg, #fff);
  border: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.08));
  border-radius: 10px;
  padding: 8px 0;
  min-width: 230px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.search_input_wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  margin: 0 8px 8px 8px;
  background-color: var(--white-opacity-6, rgba(0, 0, 0, 0.04));
  border-radius: 8px;
}

.search_icon {
  color: var(--secondary-color);
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.search_input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 13px;
  color: var(--text-color);
  font-family: 'Proxima Nova', sans-serif;
}

.search_input::placeholder {
  color: var(--secondary-color);
}

.search_select_list {
  max-height: 320px;
  overflow-y: auto;
  padding: 6px 8px 0 8px;
  border-top: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.08));
}

.search_state_block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 12px;
  min-height: 120px;
}

/* CustomLoader's built-in white spinner disappears on a white panel.
   Override via :deep() so the spinner is theme-visible here. */
.search_state_block :deep(.default-spinner) {
  border-color: var(--white-opacity-16, rgba(0, 0, 0, 0.12)) !important;
  border-top-color: var(--secondary-color, #666) !important;
}

.empty_illustration {
  width: 56px;
  height: 56px;
  opacity: 0.9;
}

.app_group:not(:first-child) {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--white-opacity-6, rgba(0, 0, 0, 0.05));
}

.app_group_header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.12s ease;
}

.app_group_header:hover {
  background-color: var(--white-opacity-6, rgba(0, 0, 0, 0.04));
}

.search_select_row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition:
    padding 0.12s ease,
    background-color 0.12s ease;
}

.search_select_row_indented {
  padding-left: 6px;
}

.search_select_row:hover {
  background-color: var(--white-opacity-6, rgba(0, 0, 0, 0.04));
}

.search_select_row_active {
  background-color: var(--white-opacity-8, rgba(0, 0, 0, 0.06));
}

.search_select_check {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.search_select_check:empty {
  display: none;
}
</style>

<style>
.usage_dropdown_overlay .ant-dropdown-menu {
  background-color: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

/* Let our panel styling control the look — strip ant-popover's inner padding/bg. */
.usage_popover_overlay .ant-popover-inner {
  background-color: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  border: none !important;
}
.usage_popover_overlay .ant-popover-inner-content {
  padding: 0 !important;
}
.usage_popover_overlay .ant-popover-arrow {
  display: none !important;
}
</style>
